import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { HeroReveal, ScrollReveal, StaggerContainer, StaggerItem, TextReveal } from '@/components/motion'
import { BRAND, halftone } from '@/components/landing/service/halftone'
import { SheetHero } from './SheetHero'
import { UseCaseTimeline, type TimelineItem } from './UseCaseTimeline'
import { PlatformCtaBand, PlatformFaq, TrialButton, hairline, ink, quoteBar, splitLastWord, type LayoutProps } from './shared'

const TILE_BG = ['var(--color-landing-accent)', '#ff00f2', 'var(--color-landing-secondary)']

/* Tile art for the timeline: small product cards over the coloured tile. */
const card = 'absolute bg-white text-[#0a0a0a] shadow-[0_18px_30px_-18px_rgb(0_0_0_/_0.5)]'

function ModelArt() {
  return (
    <div className={cn(card, 'right-[7%] top-[10%] w-[52%] text-[clamp(8px,1.9cqw,13px)]')}>
      <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] border-b border-[#e2e2e2] bg-[#f6f6f6] font-semibold uppercase text-[#5a5a5a]">
        {['', 'FY1', 'FY2', 'FY3'].map((h, i) => (
          <span key={i} className="border-r border-[#e2e2e2] px-[0.6em] py-[0.4em]">{h}</span>
        ))}
      </div>
      {[
        ['Revenue', '1.20', '1.38', '1.59'],
        ['Costs', '0.84', '0.93', '1.03'],
        ['Margin', '0.36', '0.45', '0.56'],
      ].map((row) => (
        <div key={row[0]} className="grid grid-cols-[1.4fr_1fr_1fr_1fr] border-b border-[#e2e2e2] tabular-nums">
          {row.map((c, i) => (
            <span key={i} className={cn('border-r border-[#e2e2e2] px-[0.6em] py-[0.4em]', i === 0 && 'font-semibold', row[0] === 'Margin' && i > 0 && 'bg-[#e9f6ee] text-[#107c41]')}>
              {c}
            </span>
          ))}
        </div>
      ))}
    </div>
  )
}

function AnalysisArt() {
  return (
    <div className={cn(card, 'right-[7%] top-[10%] flex h-[54%] w-[46%] items-end gap-[6%] p-[4%]')}>
      {[40, 62, 50, 78, 70, 92].map((h, i) => (
        <span key={i} className="block flex-1" style={{ height: `${h}%`, backgroundColor: i === 5 ? BRAND.pink : BRAND.blue }} />
      ))}
    </div>
  )
}

function FormulaArt({ formula }: { formula: string }) {
  return (
    <div className={cn(card, 'right-[7%] top-[12%] w-[56%] p-[3%] font-[family-name:var(--font-landing-mono)] text-[clamp(8px,1.8cqw,13px)] leading-snug')}>
      <span className="mr-2 italic text-[#898989]">fx</span>
      {formula}
    </div>
  )
}

/**
 * Excel intelligence ("giant-word"): EXCEL. at display size with a full-width
 * spreadsheet under it and the H1, lede and CTA in one row; use cases as the
 * about page's journey timeline; capabilities as a white grid on a black band
 * with the impact figures; FAQ; blue CTA.
 */
export function ExcelLayout({ feature }: LayoutProps) {
  const demoCase = feature.useCases.find((u) => u.demo)
  const demo = demoCase?.demo ?? { ask: feature.capabilities[0]?.title ?? '', answer: '' }
  const formula = /^(=.+?\))/.exec(demo.answer)?.[1] ?? '=SUMIFS(...)'
  const word = feature.shortLabel.split(' ')[0] ?? feature.shortLabel
  const [vpLead, vpLast] = splitLastWord(feature.valueProposition.title)

  const art: Record<string, ReactNode> = {
    sheet: <ModelArt />,
    chart: <AnalysisArt />,
    prompt: <FormulaArt formula={formula} />,
  }

  const items: TimelineItem[] = feature.useCases.map((u, i) => ({
    title: u.title,
    description: u.description,
    tag: u.industry,
    marker: String(i + 1).padStart(2, '0'),
    background: TILE_BG[i % TILE_BG.length]!,
    art: art[u.fragment] ?? <AnalysisArt />,
  }))

  return (
    <>
      {/* 1. Giant-word hero */}
      <section aria-labelledby="excel-title" className="overflow-x-clip pt-10 md:pt-16">
        <div className="landing-container">
          <HeroReveal delay={0} distance={30}>
            <p
              aria-hidden
              className={cn(
                'whitespace-nowrap font-[family-name:var(--font-landing-display)] text-[clamp(4rem,16vw,18rem)] uppercase leading-[1.1] tracking-[-0.04em]',
                ink
              )}
            >
              {word}
              <span className="text-[#107c41] dark:text-[#33c481]">.</span>
            </p>
          </HeroReveal>
          <HeroReveal delay={0.15} distance={40} className="-mt-[1.5vw]">
            <SheetHero title={feature.shortLabel} demo={demo} />
          </HeroReveal>

          <HeroReveal delay={0.3}>
            <div className="mt-14 grid gap-8 md:mt-20 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)_auto] lg:items-end lg:gap-14">
              <h1 id="excel-title" className={cn('landing-text-display-xl', ink)}>
                <span className="block">{feature.heroLines[0]}</span>{' '}
                <span className="block">{feature.heroLines[1]}</span>
              </h1>
              <p className={cn('landing-text-body-lg max-w-[50ch] text-pretty', quoteBar)}>{feature.subheadline}</p>
              <TrialButton className="self-start lg:self-end" />
            </div>
          </HeroReveal>

          <dl className={cn('mt-14 grid gap-6 border-y py-8 sm:grid-cols-3 md:mt-20', hairline)}>
            {feature.heroStats.map((stat) => (
              <div key={stat.label} className="flex flex-col-reverse justify-end gap-1">
                <dt className="text-base font-semibold text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">{stat.label}</dt>
                <dd className={cn('font-[family-name:var(--font-landing-display)] text-[clamp(2rem,3.4vw,3.25rem)] uppercase leading-[1.3] tracking-[-0.02em]', ink)}>
                  {stat.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* 2. Use cases as the journey timeline */}
      <UseCaseTimeline
        headingId="excel-usecases-title"
        heading={
          <>
            Built for the <span className="landing-highlight" style={{ background: '#ff00f2' }}>models</span> you run.
          </>
        }
        items={items}
      />

      {/* 3. Capabilities: white grid on a black band, then the impact figures */}
      <section aria-labelledby="excel-capabilities-title" className="landing-section-lg bg-[#0a0a0a] text-white">
        <div className="landing-container">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] lg:items-end lg:gap-20">
            <TextReveal as="h2" className="landing-text-display-xl text-white">
              <span id="excel-capabilities-title">
                {vpLead} <span className="landing-highlight">{vpLast}</span>
              </span>
            </TextReveal>
            <ScrollReveal direction="none" delay={0.15}>
              <p className="landing-text-body-lg max-w-[52ch] border-l border-white/25 pl-5 text-white/70">
                {feature.valueProposition.description}
              </p>
            </ScrollReveal>
          </div>

          <StaggerContainer amount={0.1} className="mt-16 grid gap-px bg-[#0a0a0a] sm:grid-cols-2 xl:grid-cols-4">
            {feature.capabilities.map((cap, i) => {
              const Icon = cap.icon
              const color = [BRAND.blue, BRAND.purple, BRAND.pink, '#107c41'][i % 4]!
              return (
                <StaggerItem key={cap.title} className="flex min-h-[22rem] flex-col justify-between gap-12 bg-white p-8 text-[#0a0a0a] dark:bg-[#f2f2f2] md:p-10">
                  <span aria-hidden className="flex h-14 w-14 items-center justify-center text-white" style={{ backgroundColor: color, ...halftone(0.28, 5) }}>
                    <Icon className="h-7 w-7" strokeWidth={1.75} />
                  </span>
                  <div>
                    <h3 className="font-[family-name:var(--font-landing-display)] text-lg uppercase leading-[1.4] tracking-[-0.01em] text-[#0a0a0a] md:text-xl">
                      {cap.title}
                    </h3>
                    <p className="landing-text-body-sm mt-4 text-[#0a0a0a]/70">{cap.description}</p>
                  </div>
                </StaggerItem>
              )
            })}
          </StaggerContainer>

          <h2 className="sr-only">Business impact</h2>
          <StaggerContainer amount={0.1} className="mt-20 grid gap-y-12 sm:grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-white/15">
            {feature.benefits.map((b) => (
              <StaggerItem key={b.title} className="sm:pr-8 lg:px-8 lg:first:pl-0 lg:last:pr-0">
                <p className="font-[family-name:var(--font-landing-display)] text-[clamp(2rem,3vw,3rem)] uppercase leading-[1.3] tracking-[-0.02em] text-[#ff00f2]">
                  {b.stat}
                </p>
                <h3 className="mt-3 text-lg font-semibold text-white">{b.title}</h3>
                <p className="landing-text-body-sm mt-2 text-white/65">{b.description}</p>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      <PlatformFaq feature={feature} />
      <PlatformCtaBand feature={feature} tone="blue" />
    </>
  )
}
